import { useCallback, useEffect, useState } from 'react';
import {
  BOOKMARKS_UPDATED_EVENT,
  BOOKMARKED_JOB_IDS_STORAGE_KEY,
  getBookmarkedJobIds,
  toggleBookmark as toggleBookmarkInStorage,
} from 'javascripts/utils/bookmarks';

/**
 * Manages bookmarked job IDs backed by localStorage.
 * Syncs across browser tabs (via storage events) and across same-page
 * components (via the 'bookmarks:updated' custom event).
 */
export default function useBookmarks(): [
  string[],
  (jobId: string) => void,
] {
  const [bookmarks, setBookmarks] = useState<string[]>(() =>
    getBookmarkedJobIds(),
  );

  const syncBookmarks = useCallback(() => {
    setBookmarks(getBookmarkedJobIds());
  }, []);

  const toggleBookmark = useCallback(
    (jobId: string) => {
      toggleBookmarkInStorage(jobId);
      syncBookmarks();
    },
    [syncBookmarks],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key === BOOKMARKED_JOB_IDS_STORAGE_KEY) {
        syncBookmarks();
      }
    };

    const onBookmarksUpdated = () => {
      syncBookmarks();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(BOOKMARKS_UPDATED_EVENT, onBookmarksUpdated);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(BOOKMARKS_UPDATED_EVENT, onBookmarksUpdated);
    };
  }, [syncBookmarks]);

  return [bookmarks, toggleBookmark];
}
